import React, { useState, useEffect } from "react";
import CalendarView from "../components/calendar/CalendarView";
import { getStaff } from "../services/staffService";
import { getRooms } from "../services/roomService";
import { useTenant } from "../context/TenantContext";
import { useAuth } from "../context/AuthContext";

// Appointment types - adapt as needed
const typeOptions = [
  "Physiotherapy",
  "Personal Training",
  "Massage",
  "Other"
];

function Calendar() {
  const { currentTenant, loading: tenantLoading } = useTenant();
  const { currentUser, userProfile } = useAuth();
  
  // Filter state - default to current user's ID for staff filter
  const [filterStaff, setFilterStaff] = useState("current");
  const [filterRoom, setFilterRoom] = useState("all");
  const [filterType, setFilterType] = useState("all");
  
  // Staff and room lists
  const [staffList, setStaffList] = useState([]);
  const [roomList, setRoomList] = useState([]);
  
  // State for handling URL parameters
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  
  // Load staff and rooms when tenant context is ready
  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      const loadData = async () => {
        try {
          const [staff, rooms] = await Promise.all([
            getStaff(),
            getRooms()
          ]);
          console.log('📋 Loaded staff for calendar filter:', staff);
          if (staff && staff.length > 0) {
            console.log('📋 Staff data structure:', staff.map(s => ({
              id: s.id,
              name: s.name,
              fullName: s.fullName,
              firstName: s.firstName,
              lastName: s.lastName,
              email: s.email,
              role: s.role,
              allFields: Object.keys(s)
            })));
          }
          setStaffList(Array.isArray(staff) ? staff : []);
          setRoomList(Array.isArray(rooms) ? rooms : []);
        } catch (error) {
          console.error('Error loading calendar data:', error);
          setStaffList([]);
          setRoomList([]);
        }
      };
      
      loadData();
    }
  }, [tenantLoading, currentTenant]);

  // Handle URL parameters for opening appointment modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const action = urlParams.get('action');
    const type = urlParams.get('type');
    
    console.log('📋 Calendar: Checking URL params:', { action, type });
    
    if (action === 'add' && type === 'leave') {
      console.log('🎯 Calendar: Setting up leave modal trigger');
      setModalType('leave');
      setShouldOpenModal(true);
      
      // Clear URL parameters after a short delay to ensure CalendarView receives the props
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 1000);
    }
  }, []);

  return (
    <div className="space-y-6 p-6">
      {/* Modern Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Staff Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Staff</label>
              <select
                value={filterStaff}
                onChange={e => setFilterStaff(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 min-w-[140px]"
              >
                <option value="current">My Calendar</option>
                <option value="all">All Staff</option>
                {staffList.map(s => {
                  // Use same display name logic as AppointmentModal
                  const displayName = s.name || s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email || 'Unnamed Staff';
                  return (
                    <option key={s.id} value={s.id}>{displayName}</option>
                  );
                })}
              </select>
            </div>
            
            {/* Room Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Room</label>
              <select
                value={filterRoom}
                onChange={e => setFilterRoom(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 min-w-[140px]"
              >
                <option value="all">All Rooms</option>
                {roomList.map(r => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </div>
            
            {/* Type Filter */}
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">Type</label>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 min-w-[140px]"
              >
                <option value="all">All Types</option>
                {typeOptions.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Clear Filters Button */}
          <button
            className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors duration-200 border border-gray-300 font-medium"
            onClick={() => {
              setFilterStaff("current");
              setFilterRoom("all");
              setFilterType("all");
            }}
          >
            Reset Filters
          </button>
        </div>
      </div>

      <CalendarView
        appointments={[]}
        staff={staffList}
        clients={[]}
        filterStaff={filterStaff === "current" ? currentUser?.uid : filterStaff}
        filterRoom={filterRoom}
        filterType={filterType}
        selectedDate={null}
        onDateChange={null}
        shouldOpenModal={shouldOpenModal}
        modalType={modalType}
        onModalOpened={() => {
          setShouldOpenModal(false);
          setModalType(null);
        }}
      />
      
      {/* Debug info */}
      {process.env.NODE_ENV === 'development' && (
        <div className="fixed bottom-4 right-4 bg-black text-white p-2 rounded text-xs">
          Modal Trigger: {shouldOpenModal ? 'YES' : 'NO'} | Type: {modalType || 'none'}
        </div>
      )}
    </div>
  );
}

export default Calendar;
